import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import BlogForm from './BlogForm';
import { BlogApi } from '../../api/adminApi';
import { primeBlogCache } from '../../hooks/useBlogs';
import { useBlogTopics } from '../../hooks/useBlogTopics';
import './BlogsAdmin.css';

const BlogsAdmin = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { topics, isLoading: topicsLoading } = useBlogTopics();

  const loadBlogs = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await BlogApi.getAll();
      setBlogs(data);
      primeBlogCache(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  const handleCreate = () => {
    setCurrentBlog(null);
    setIsFormOpen(true);
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setIsFormOpen(true);
  };

  const handleDelete = async (blog) => {
    const confirmed = window.confirm(`Delete blog "${blog.title}"?`);
    if (!confirmed) return;
    try {
      await BlogApi.remove(blog.id);
      await loadBlogs();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to delete blog');
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentBlog(null);
  };

  const handleSubmit = async (payload) => {
    setIsSaving(true);
    try {
      if (currentBlog) {
        await BlogApi.update(currentBlog.id, payload);
      } else {
        await BlogApi.create(payload);
      }
      await loadBlogs();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save blog');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    closeForm();
  };

  return (
    <AdminLayout
      title="Blogs"
      description="Publish, edit, and organize all marketing articles."
      actions={
        !isFormOpen && (
          <>
            <button className="admin-primary-btn" onClick={handleCreate}>+ Add Blog</button>
            <Link className="admin-secondary-btn" to="/admin/blog-topics">
              Manage Topics
            </Link>
          </>
        )
      }
    >
      {error && <div className="admin-alert">{error}</div>}

      {isFormOpen ? (
        <BlogForm
          initialData={currentBlog}
          topics={topics}
          topicsLoading={topicsLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      ) : (
        <div className="services-table-wrapper">
          {loading ? (
            <p>Loading blogs…</p>
          ) : (
            <table className="services-table blogs-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Category</th>
                  <th>Topics</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.length === 0 && (
                  <tr>
                    <td colSpan="6">No blog posts yet.</td>
                  </tr>
                )}
                {blogs.map((blog) => (
                  <tr key={blog.id}>
                    <td>{blog.title}</td>
                    <td>{blog.author}</td>
                    <td>{blog.category}</td>
                    <td>
                      <div className="blog-topic-list">
                        {(blog.topics || []).map((topic) => (
                          <span key={topic.id} className="blog-topic-chip">{topic.name}</span>
                        ))}
                        {(blog.topics || []).length === 0 && <span className="helper-text">None</span>}
                      </div>
                    </td>
                    <td>{blog.updatedAt ? new Date(blog.updatedAt).toLocaleString() : '-'}</td>
                    <td>
                      <div className="table-actions">
                        <button type="button" onClick={() => handleEdit(blog)}>Edit</button>
                        <button type="button" className="danger" onClick={() => handleDelete(blog)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default BlogsAdmin;
